import { useState } from 'react';

import { communityApi } from '../../api/community';
import type { PostAttachment, PostType, PostView } from '../../api/community.types';
import { progressApi } from '../../api/progress';
import type { ProgressView } from '../../api/progress.types';
import { Button } from '../../ds';
import { AuthorRow } from '../social/AuthorRow';
import { POST_TYPE_LABEL } from '../social/postParts';
import { useSocial } from '../social/SocialContext';
import { SocialHeader } from '../social/SocialHeader';
import { SocialIcon, SocialIconButton, type SocialIconName } from '../social/SocialIcon';

const CUSTOM_TOPIC_MAX = 30;

/// Loại người dùng tự đăng được — Thành tích do hệ thống tạo, không có trong danh sách.
export type ComposableType = Exclude<PostType, 'ACHIEVEMENT'>;

const TYPES: { type: ComposableType; description: string; placeholder: string }[] = [
  { type: 'QA', description: 'Hỏi về chữ, từ, ngữ pháp', placeholder: 'Ví dụ: Khi nào dùng に và khi nào dùng で?' },
  { type: 'SHARE', description: 'Mẹo học, bộ thẻ, tài liệu', placeholder: 'Mẹo học hoặc tài liệu bạn thấy hữu ích…' },
  { type: 'FIND_BUDDY', description: 'Lập nhóm cùng học theo giờ', placeholder: 'Bạn học bài nào, giờ nào, cần mấy người?' },
  { type: 'APP_SUPPORT', description: 'Báo lỗi, góp ý, hỏi cách dùng app', placeholder: 'Ví dụ: Thẻ SRS không hiện âm thanh khi ôn tập…' },
  { type: 'OTHER', description: 'Tự đặt tên chủ đề', placeholder: 'Viết nội dung bài…' },
];

/** Nhãn "Bài đang học" lấy từ tiến độ: Bài có nhánh ở trạng thái 'current'. */
function currentLesson(progress: ProgressView): string | null {
  for (const [bai, branch] of Object.entries(progress.branch)) {
    if (branch.vocab.state === 'current') return `Bài ${bai} · Từ vựng`;
    if (branch.grammar.state === 'current') return `Bài ${bai} · Ngữ pháp`;
    if (branch.exercise.state === 'current') return `Bài ${bai} · Bài tập`;
  }
  return null;
}

function attachmentLabel(attachment: PostAttachment) {
  if (attachment.kind === 'card') return `${attachment.jp} — ${attachment.vi}`;
  return attachment.kind === 'lesson' ? attachment.title : '';
}

function AttachChip({ icon, label, onClick }: { icon: SocialIconName; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="sk-press-page"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 44,
        padding: '0 14px',
        borderRadius: 999,
        border: '1px solid var(--border-default)',
        cursor: 'pointer',
        fontFamily: 'var(--font-vi)',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--ink)',
      }}
    >
      <SocialIcon name={icon} size={17} color="var(--cta-default)" />
      {label}
    </button>
  );
}

/** SCR-COM-03 trong modal: chọn loại bài, nội dung, đính kèm. */
export function ComposePostPanel({ initialType = 'QA', onClose, onPosted }: { initialType?: ComposableType; onClose: () => void; onPosted: (post: PostView) => void }) {
  const { me, toast } = useSocial();
  const [type, setType] = useState<ComposableType>(initialType);
  const [customTopic, setCustomTopic] = useState('');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState<PostAttachment | null>(null);
  const [posting, setPosting] = useState(false);

  const isOther = type === 'OTHER';
  const canPost = body.trim().length > 0 && (!isOther || customTopic.trim().length > 0) && !posting;

  const attachLesson = async () => {
    try {
      const title = currentLesson(await progressApi.getProgress());
      if (title) setAttachment({ kind: 'lesson', title });
      else toast('Bạn chưa có bài nào đang học');
    } catch {
      toast('Không tải được bài đang học');
    }
  };

  const post = async () => {
    if (!canPost) return;
    setPosting(true);
    try {
      const created = await communityApi.createPost({
        type,
        body: body.trim(),
        ...(isOther ? { customTopic: customTopic.trim() } : {}),
        ...(attachment ? { attachment } : {}),
      });
      onPosted(created);
    } catch {
      toast('Chưa đăng được bài, thử lại sau');
      setPosting(false);
    }
  };

  const field = {
    width: '100%',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-control)',
    fontFamily: 'var(--font-vi)',
    color: 'var(--ink)',
    outline: 'none',
    background: 'var(--surface-page)',
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'min(720px, 86vh)', maxHeight: '100%', background: 'var(--surface-page)' }}>
      <SocialHeader
        title="Tạo bài viết"
        left={<SocialIconButton name="close" label="Đóng" onClick={onClose} />}
        right={
          <div style={{ paddingRight: 8 }}>
            <Button size="sm" disabled={!canPost} onClick={post}>
              Đăng
            </Button>
          </div>
        }
      />
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {me && <AuthorRow author={me} />}

        <div role="radiogroup" aria-label="Loại bài" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Loại bài</div>
          {TYPES.map((t) => {
            const on = type === t.type;
            return (
              <button
                key={t.type}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setType(t.type)}
                className={on ? undefined : 'sk-press-page'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  minHeight: 52,
                  borderRadius: 'var(--radius-control)',
                  border: on ? '1.5px solid var(--cta-default)' : '1px solid var(--border-default)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--font-vi)',
                  ...(on ? { background: 'var(--surface-tint)' } : {}),
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: on ? '6px solid var(--cta-default)' : '2px solid var(--navy-100)',
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{POST_TYPE_LABEL[t.type]}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>{t.description}</span>
                </span>
              </button>
            );
          })}
          {isOther && (
            <input
              autoFocus
              className="sk-input"
              aria-label="Tên chủ đề"
              value={customTopic}
              maxLength={CUSTOM_TOPIC_MAX}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="Tên chủ đề, ví dụ: Văn hoá Nhật"
              style={{ ...field, height: 44, padding: '0 14px', fontSize: 15, borderColor: 'var(--cta-default)' }}
            />
          )}
        </div>

        <textarea
          className="sk-input"
          aria-label="Nội dung bài"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={TYPES.find((t) => t.type === type)?.placeholder}
          rows={5}
          style={{ ...field, resize: 'vertical', minHeight: 124, padding: 14, fontSize: 16, lineHeight: 1.5 }}
        />

        {attachment && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '2px 0 2px 12px',
              borderRadius: 12,
              background: 'var(--surface-tint)',
              border: '1px solid var(--border-default)',
            }}
          >
            <SocialIcon name={attachment.kind === 'lesson' ? 'book' : 'card'} size={20} color="var(--cta-default)" />
            <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{attachmentLabel(attachment)}</div>
            <SocialIconButton name="close" label="Bỏ đính kèm" color="var(--text-secondary)" onClick={() => setAttachment(null)} />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>Đính kèm</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <AttachChip icon="book" label="Bài đang học" onClick={attachLesson} />
            <AttachChip icon="image" label="Ảnh" onClick={() => toast('Đính kèm ảnh sắp có')} />
          </div>
        </div>

        <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          Hãy lịch sự và tôn trọng mọi người. Bài vi phạm có thể bị ẩn sau khi bị báo cáo.
        </div>
      </div>
    </div>
  );
}
